import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CrudTable from "@/components/CrudTable";
import { MODULES } from "@/config/modules";
import { useLookups } from "@/hooks/useLookups";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, X, FloppyDisk } from "@phosphor-icons/react";

export default function Settings() {
  const { lists, nameMap, team, reload } = useLookups();
  return (
    <div data-testid="settings-page">
      <Tabs defaultValue="targets">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="targets" data-testid="tab-targets">Revenue &amp; Config</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team Master List</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Change Log</TabsTrigger>
        </TabsList>

        <TabsContent value="targets"><ConfigPanel /></TabsContent>
        <TabsContent value="team">
          <p className="text-sm text-slate mb-4 max-w-3xl">
            Add a team member here and they instantly appear in the relevant module dropdowns and leaderboards.
            Employee ID is auto-generated and stable — all data joins on it, never the display name.
          </p>
          <CrudTable resource="team_members" config={MODULES.team_members} lists={lists} nameMap={nameMap} onData={reload} />
        </TabsContent>
        <TabsContent value="log"><ChangeLog /></TabsContent>
      </Tabs>
    </div>
  );
}

function TagList({ label, items, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } };
  return (
    <div>
      <Label className="text-sm font-semibold text-navy mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full pl-3 pr-1.5 py-1 text-sm">
            {it}
            <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="h-5 w-5 rounded-full hover:bg-rose/10 flex items-center justify-center text-slate hover:text-rose"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={`Add ${label.toLowerCase()}…`} className="h-9 focus-visible:ring-gold" />
        <Button onClick={add} variant="outline" size="icon" className="h-9 w-9 shrink-0"><Plus size={16} /></Button>
      </div>
    </div>
  );
}

function ConfigPanel() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.get("/config/settings").then((r) => setCfg(r.data)); }, []);

  const save = async () => {
    try {
      await api.put("/config/settings", cfg);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  if (!cfg) return <div className="text-slate">Loading…</div>;
  const targets = cfg.revenue_targets || {};

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-bold text-navy">Mission</h3>
        <Textarea value={cfg.mission || ""} onChange={(e) => setCfg({ ...cfg, mission: e.target.value })} rows={2} className="focus-visible:ring-gold" data-testid="settings-mission" />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-bold text-navy">Monthly Revenue Targets (₹)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(targets).sort().map(([m, v]) => (
            <div key={m}>
              <Label className="text-xs text-slate mb-1 block">{m}</Label>
              <Input type="number" value={v} onChange={(e) => setCfg({ ...cfg, revenue_targets: { ...targets, [m]: Number(e.target.value) } })} className="h-9 focus-visible:ring-gold" data-testid={`target-${m}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
        <TagList label="Courses" items={cfg.courses || []} onChange={(v) => setCfg({ ...cfg, courses: v })} testid="add-course" />
        <TagList label="Lead Sources" items={cfg.lead_sources || []} onChange={(v) => setCfg({ ...cfg, lead_sources: v })} testid="add-source" />
        <TagList label="Marketing Channels" items={cfg.channels || []} onChange={(v) => setCfg({ ...cfg, channels: v })} testid="add-channel" />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-navy mb-2 block">Occupancy Alert Threshold (%)</Label>
          <Input type="number" value={cfg.occupancy_alert_threshold || 75} onChange={(e) => setCfg({ ...cfg, occupancy_alert_threshold: Number(e.target.value) })} className="h-9 focus-visible:ring-gold" data-testid="threshold-occupancy" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-navy mb-2 block">Payment Aging Alert (days)</Label>
          <Input type="number" value={cfg.payment_aging_alert_days || 30} onChange={(e) => setCfg({ ...cfg, payment_aging_alert_days: Number(e.target.value) })} className="h-9 focus-visible:ring-gold" data-testid="threshold-aging" />
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="save-settings-button" onClick={save} className="bg-navy hover:bg-navy/90 text-white gap-1.5"><FloppyDisk size={16} /> Save Configuration</Button>
      </div>
    </div>
  );
}

function ChangeLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/change-log").then((r) => setLogs(r.data)); }, []);
  const color = { create: "text-emerald bg-emerald/10", update: "text-gold bg-gold/10", delete: "text-rose bg-rose/10" };
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <h3 className="font-heading font-bold text-navy mb-4">Change Log — who changed what, when</h3>
      <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="change-log-list">
        {logs.length === 0 && <p className="text-sm text-slate">No changes logged yet.</p>}
        {logs.map((l) => (
          <div key={l.id} className="flex items-center gap-3 py-2 border-b border-slate-100 text-sm">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${color[l.action] || "bg-slate-100"}`}>{l.action}</span>
            <span className="font-medium text-navy">{l.user_name}</span>
            <span className="text-slate">{l.action}d in <span className="font-medium">{l.resource}</span> — {l.summary}</span>
            <span className="ml-auto text-xs text-slate">{new Date(l.timestamp).toLocaleString("en-IN")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
